/**
 * `AuditChangesRpc`: the firm's poll of what changed since its cursor, over
 * `cova_changes.changes_since` (spec §5.3, plan ruling 3). Effect RPC, not
 * HttpApi: the door is the platform's, not the BMS book's (spec D1).
 *
 * ONE READ AND NO WRITE. It appends nothing, so it carries no actor
 * middleware.
 *
 * THE FIRM IS NEVER IN THE PAYLOAD (harness row 24). The handler reads the
 * caller's organisation from `AuthorisedUserContext`; the payload is the cursor
 * pair alone, so a caller cannot name another firm.
 *
 * MIDDLEWARE, in order, as the ledger's group has it less the actor:
 * `UserMiddleware` (who is asking, and their organisation),
 * `RequestIdentityMiddleware` (the span identity every authenticated group
 * carries), then `WireErrorSanitizerMiddleware` last, so an unmapped failure
 * reaches the browser as an opaque `INTERNAL` with a trace id.
 */
import { Rpc, RpcGroup } from "@effect/rpc"
import { Schema as S } from "effect"

import { AuthorisationError } from "@/application/shared/schemas/authorisation-schemas"
import { AllowedApps } from "@/presentation/rpc/middleware/allowed-apps"
import { RequestIdentityMiddleware } from "@/presentation/rpc/middleware/request-identity-middleware"
import { UserMiddleware } from "@/presentation/rpc/middleware/user-middleware"
import { WireErrorSanitizerMiddleware } from "@/shared/error-diagnostics/wire"

import { ChangesCursor, ChangesPage, ChangesXids } from "../domain/changes-page"
import { AuditChangesWireError } from "./audit-changes-wire-errors"

export const AuditChangesRpcError = S.Union(AuthorisationError, AuditChangesWireError)

export const ChangesSinceRpc = Rpc.make("changesSince", {
  /** Omitted on a fresh tab: the page then carries both tails and no tables. */
  payload: {
    cursor: S.optional(ChangesCursor),
    /** The tab's own recent transaction ids (query speed Q11); the page marks their tables `echoed`. */
    xids: S.optional(ChangesXids),
  },
  success: ChangesPage,
  error: AuditChangesRpcError,
})

export const AuditChangesRpc = RpcGroup.make(ChangesSinceRpc)
  .annotateRpcs(AllowedApps, ["platform", "bots"])
  .middleware(UserMiddleware)
  .middleware(RequestIdentityMiddleware)
  .middleware(WireErrorSanitizerMiddleware)
